/* =====================================================================
   Bluewud × Empire v13 — Collection filter / sort enhancer.
   The native Empire filter JS is inert in this build, so this self-contained
   controller wires the bits that were dead:
     • Sort By        → navigate with ?sort_by= the moment a new option is
                        chosen (auto-applies; no need to also touch a filter).
     • Price range    → navigate with the min/max filter params automatically
                        (debounced as you type, on Enter, and on submit).
     • Group collapse → the filter heading buttons toggle their group open/closed.
   Colour / list filters already work — they are plain <a href> links.
   All actions PRESERVE the other active params (sort + every filter) because
   we mutate the current URL rather than replacing it.
   ===================================================================== */
(function () {
  'use strict';

  function currentUrl() { return new URL(window.location.href); }
  function go(url) { url.searchParams.delete('page'); window.location.href = url.toString(); }

  // ---- Sort By (desktop <select> + mobile modal buttons) -------------------
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-productgrid-trigger-sort]');
    if (!sel) return;
    var url = currentUrl();
    url.searchParams.set('sort_by', sel.value);
    go(url);
  });

  // ---- Price range: collect both inputs, set their params, navigate --------
  function applyPrice(scope) {
    var root = scope && scope.closest
      ? (scope.closest('.collection-filters__price-range-form') || document)
      : document;
    var url = currentUrl();
    root.querySelectorAll('[data-filter-range]').forEach(function (inp) {
      var name = inp.getAttribute('name');
      if (!name) return;
      var val = (inp.value || '').replace(/[^0-9.]/g, '');
      if (val) url.searchParams.set(name, val);
      else url.searchParams.delete(name);
    });
    go(url);
  }

  // Auto-apply price. The shopper needs time to fill BOTH the min and max boxes
  // before the page reloads, so:
  //   • the typing debounce is long (≈3× the old delay), and it will NOT fire
  //     while the cursor is still inside a price box — so entering the first
  //     amount no longer reloads the page mid-entry (which used to apply a
  //     min-only filter and snap the range to the catalogue's highest price);
  //   • it applies immediately when they leave the price filter (tab/click
  //     away — but NOT when moving between the From and To boxes), or on Enter.
  var PRICE_DEBOUNCE = 2800; // ms
  var priceTimer;
  function inPriceBox() {
    var ae = document.activeElement;
    return !!(ae && ae.closest && ae.closest('[data-filter-range]'));
  }
  function schedulePrice(inp) {
    clearTimeout(priceTimer);
    priceTimer = setTimeout(function () {
      // Still typing? hold off — leaving the field (focusout) or Enter applies.
      if (inPriceBox()) { schedulePrice(inp); return; }
      applyPrice(inp);
    }, PRICE_DEBOUNCE);
  }
  document.addEventListener('input', function (e) {
    var inp = e.target.closest('[data-filter-range]');
    if (!inp) return;
    schedulePrice(inp);
  });
  // Apply the moment focus leaves the price filter entirely (not when hopping
  // from the From box to the To box) so "enter both, then click away" loads.
  document.addEventListener('focusout', function (e) {
    var inp = e.target.closest('[data-filter-range]');
    if (!inp) return;
    var form = inp.closest('.collection-filters__price-range-form');
    var next = e.relatedTarget;
    if (form && next && form.contains(next)) return; // moving From <-> To: wait
    var anyVal = false;
    (form || document).querySelectorAll('[data-filter-range]').forEach(function (i) {
      if ((i.value || '').trim()) anyVal = true;
    });
    if (!anyVal) return; // nothing entered — don't reload
    clearTimeout(priceTimer);
    applyPrice(inp);
  });
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('.collection-filters__price-range-form');
    if (!form) return;
    e.preventDefault();
    clearTimeout(priceTimer);
    applyPrice(form);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var inp = e.target.closest('[data-filter-range]');
    if (!inp) return;
    e.preventDefault();
    clearTimeout(priceTimer);
    applyPrice(inp);
  });

  // ---- Filter group collapse (heading button toggles the accordion) -------
  document.addEventListener('click', function (e) {
    // Mobile sort modal buttons.
    var sortBtn = e.target.closest('[data-productgrid-trigger-sort-button]');
    if (sortBtn && sortBtn.value) {
      e.preventDefault();
      var surl = currentUrl();
      surl.searchParams.set('sort_by', sortBtn.value);
      go(surl);
      return;
    }

    var trigger = e.target.closest('[data-filter-group-trigger]');
    if (!trigger) return;
    e.preventDefault();
    var group = trigger.closest('[data-filter-group]');
    if (!group) return;
    var content = group.querySelector('[data-accordion-content]');
    if (!content) return;
    var isOpen = content.getAttribute('data-accordion-state') !== 'closed';
    content.setAttribute('data-accordion-state', isOpen ? 'closed' : 'open');
    trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  });

  // ---- Init: groups start OPEN with consistent aria, so the first click of
  //      a heading reliably collapses it. (defer = DOM is ready here.) -------
  document.querySelectorAll('[data-filter-group]').forEach(function (group) {
    var trigger = group.querySelector('[data-filter-group-trigger]');
    var content = group.querySelector('[data-accordion-content]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (content) content.setAttribute('data-accordion-state', 'open');
  });

  // ---- Mobile "Filters" / "Sort by" sheets ---------------------------------
  // Empire's own JS never binds these triggers in this build — clicking them did
  // nothing at all — and both panels ship as display:none on mobile. The panels
  // themselves are fine (colour filters are plain links; price + sort are wired
  // above), so all that was missing is the open/close. Own it: toggle a root
  // class that the CSS turns into a bottom sheet.
  (function sheets() {
    var root = document.documentElement;
    var backdrop = null;

    function closeAll() {
      root.classList.remove('bw-sheet--filters', 'bw-sheet--sort');
      if (backdrop) backdrop.classList.remove('is-open');
    }

    function ensureBackdrop() {
      if (backdrop) return backdrop;
      backdrop = document.createElement('div');
      backdrop.className = 'bw-sheet-backdrop';
      backdrop.addEventListener('click', closeAll);
      document.body.appendChild(backdrop);
      return backdrop;
    }

    // Give each panel a sticky header (grab handle + title + close) once.
    function decorate(panel, title, withDone) {
      if (!panel || panel.__bwSheet) return;
      panel.__bwSheet = true;
      var head = document.createElement('div');
      head.className = 'bw-sheet-head';
      var h = document.createElement('h2');
      h.className = 'bw-sheet-head__title';
      h.textContent = title;
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'bw-sheet-close';
      x.setAttribute('aria-label', 'Close ' + title);
      x.innerHTML = '&#10005;';
      x.addEventListener('click', closeAll);
      head.appendChild(h);
      head.appendChild(x);
      panel.insertBefore(head, panel.firstChild);

      if (withDone) {
        var done = document.createElement('div');
        done.className = 'bw-sheet-done';
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = 'Show results';
        b.addEventListener('click', closeAll);
        done.appendChild(b);
        panel.appendChild(done);
      }
    }

    function open(which) {
      var panel = which === 'filters'
        ? document.querySelector('.productgrid--sidebar')
        : document.querySelector('.utils-sortby--modal');
      if (!panel) return;
      decorate(panel, which === 'filters' ? 'Filters' : 'Sort by', which === 'filters');
      ensureBackdrop().classList.add('is-open');
      root.classList.remove('bw-sheet--filters', 'bw-sheet--sort');
      root.classList.add('bw-sheet--' + which);
      panel.scrollTop = 0;
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-productgrid-trigger-filters]')) { e.preventDefault(); open('filters'); return; }
      if (e.target.closest('.utils-sortby-button')) { e.preventDefault(); open('sort'); return; }
      // Picking a sort option navigates (handled above) — close so it never lingers.
      if (e.target.closest('[data-productgrid-trigger-sort-button]')) closeAll();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') closeAll();
    });
  })();
})();
